import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, Upload, RefreshCw, CheckCircle2, AlertTriangle, ScanLine, Cpu, FileText, Copy } from "lucide-react";

const GUIDANCE_STYLE = {
  ready_for_confirmation: { label: "Ready for confirmation", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  human_review: { label: "Needs human review", tone: "bg-amber-100 text-amber-900 border-amber-200" },
  reupload: { label: "Please re-upload", tone: "bg-orange-100 text-orange-900 border-orange-200" },
  not_prescription: { label: "Not a prescription", tone: "bg-slate-100 text-slate-800 border-slate-200" },
};

function GuidanceBadge({ guidance }) {
  if (!guidance) return null;
  const st = GUIDANCE_STYLE[guidance.action] || { label: guidance.action, tone: "bg-slate-100 text-slate-800 border-slate-200" };
  return (
    <div data-testid="rx-guidance" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${st.tone}`}>
      <ScanLine size={14} />
      <span className="font-medium">{st.label}</span>
      <span className="opacity-80">— {guidance.message}</span>
    </div>
  );
}

function ProviderChip({ runs = [], primary, fallbackUsed }) {
  if (!runs?.length) return null;
  const total = runs.reduce((s, r) => s + (r?.processing_ms || 0), 0);
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
      <Badge variant="outline" className="gap-1"><Cpu size={12} /> {primary || "provider"}</Badge>
      {fallbackUsed && <Badge className="bg-amber-100 text-amber-900 border-amber-200 border">fallback used</Badge>}
      <span data-testid="rx-provider-runs">{runs.length} model call(s) · {total} ms total</span>
    </div>
  );
}

function MedicineRow({ m, index, onChange, onRemove }) {
  const flagged = m.needs_human_verification;
  const isDupe = (m.alternatives || []).length > 0;
  return (
    <div
      data-testid={`rx-med-row-${index}`}
      className={`grid grid-cols-1 md:grid-cols-12 gap-2 rounded-lg border p-3 ${isDupe ? "border-rose-300 bg-rose-50/40" : flagged ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}
    >
      <div className="md:col-span-3">
        <Label className="text-xs text-slate-500">Name</Label>
        <Input data-testid={`rx-med-name-${index}`} value={m.name || ""} onChange={(e) => onChange({ ...m, name: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500">Strength</Label>
        <Input data-testid={`rx-med-strength-${index}`} value={m.strength || ""} onChange={(e) => onChange({ ...m, strength: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500">Form</Label>
        <Input data-testid={`rx-med-form-${index}`} value={m.dosage_form || ""} onChange={(e) => onChange({ ...m, dosage_form: e.target.value })} />
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs text-slate-500">Freq</Label>
        <Input data-testid={`rx-med-freq-${index}`} value={m.frequency || ""} onChange={(e) => onChange({ ...m, frequency: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs text-slate-500">Duration</Label>
        <Input data-testid={`rx-med-duration-${index}`} value={m.duration || ""} onChange={(e) => onChange({ ...m, duration: e.target.value })} />
      </div>
      <div className="md:col-span-2 flex flex-col justify-between">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" data-testid={`rx-med-confidence-${index}`}>conf {(m.confidence ?? 0).toFixed(2)}</Badge>
          {m.page_index !== null && m.page_index !== undefined && <Badge variant="outline">page {m.page_index + 1}</Badge>}
          {isDupe && (
            <Badge className="bg-rose-100 text-rose-900 border-rose-200 border" data-testid={`rx-med-duplicate-${index}`}>
              <Copy size={10} className="mr-1" /> duplicate x{(m.alternatives || []).length + 1}
            </Badge>
          )}
          {flagged && !isDupe && (
            <Badge className="bg-amber-100 text-amber-900 border-amber-200 border">
              <AlertTriangle size={10} className="mr-1" /> review
            </Badge>
          )}
        </div>
        <Button data-testid={`rx-med-remove-${index}`} size="sm" variant="ghost" className="self-end" onClick={() => onRemove(index)}>
          Remove
        </Button>
      </div>
      {(m.master_matches?.length || m.hints?.length) ? (
        <div className="md:col-span-12 text-xs text-slate-500 space-y-1">
          {m.master_matches?.length ? (
            <div>Knowledge matches: {m.master_matches.map((mm) => `${mm.master_name} (${mm.similarity}${mm.confirmed_count ? `, seen ${mm.confirmed_count}x` : ""})`).join(", ")}</div>
          ) : null}
          {m.hints?.length ? (
            <div className="italic">Seed hints: {m.hints.map((mm) => `${mm.master_name} (${mm.similarity})`).join(", ")}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function PrescriptionAI() {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [memberPhone, setMemberPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [meds, setMeds] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);

  const refreshHealth = async () => {
    try {
      const { data } = await api.get("/prescription-ai/health");
      setHealth(data);
    } catch (err) { console.warn("prescription-ai health check failed:", err); }
  };

  const refreshRecent = async () => {
    try {
      const { data } = await api.get("/prescription-ai/documents", { params: { limit: 10 } });
      setRecent(data?.rows || []);
    } catch (err) { console.warn("prescription-ai recent fetch failed:", err); }
  };

  useEffect(() => { refreshHealth(); refreshRecent(); }, []);

  const onAnalyse = async (e) => {
    e?.preventDefault?.();
    if (!files || files.length === 0) { toast.error("Choose at least one image or PDF"); return; }
    setLoading(true);
    setResult(null); setMeds([]);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      if (memberPhone) fd.append("member_phone", memberPhone);
      const { data } = await api.post("/prescription-ai/analyse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setMeds((data?.extraction?.medicines || []).map((m) => ({ ...m })));
      toast.success(`Analysed ${data?.file_count || 0} file(s), ${data?.pages?.length || 0} page(s)`);
      refreshRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to analyse");
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    if (!result?.id) return;
    try {
      const payload = { medicines: meds.map(({ master_matches, ...rest }) => rest), remarks };
      const { data } = await api.post(`/prescription-ai/documents/${result.id}/confirm`, payload);
      setResult(data);
      toast.success("Confirmed and locked");
      refreshRecent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Confirm failed");
    }
  };

  const summary = useMemo(() => {
    if (!result) return null;
    return {
      classification: result.classification,
      conf: result.confidence_score,
      guidance: result.guidance,
      provider: result.primary_provider,
      fallback: result.fallback_used,
      runs: result.provider_runs || [],
    };
  }, [result]);

  return (
    <div className="p-6 space-y-6" data-testid="prescription-ai-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Prescription Understanding</h1>
          <p className="text-sm text-slate-500 mt-1">
            Multimodal image classification + structured medicine extraction. Every low-confidence result is held for staff review.
          </p>
        </div>
        {health && (
          <div className="text-xs text-slate-500 text-right">
            <div>primary: <span className="font-mono">{health.primary}</span></div>
            <div>fallback: <span className="font-mono">{health.fallback}</span></div>
            <div>threshold: {health.threshold}</div>
          </div>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Upload prescription</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={onAnalyse} className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <Label>Images / PDF (single or multi-page)</Label>
              <Input ref={fileRef} data-testid="rx-file-input" type="file" accept="image/*,application/pdf" multiple
                     onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              {files.length > 0 && (
                <div className="text-xs text-slate-500 mt-1" data-testid="rx-selected-files">
                  {files.length} file(s): {files.map((f) => f.name).join(", ")}
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <Label>Member phone (optional)</Label>
              <Input data-testid="rx-member-phone" placeholder="+91…" value={memberPhone}
                     onChange={(e) => setMemberPhone(e.target.value)} />
            </div>
            <div className="md:col-span-4 flex items-end gap-2">
              <Button data-testid="rx-analyse-btn" type="submit" disabled={loading || files.length === 0}>
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Analyse
              </Button>
              <Button data-testid="rx-refresh-btn" type="button" variant="outline" onClick={() => { setFiles([]); setResult(null); setMeds([]); if (fileRef.current) fileRef.current.value = ""; }}>
                <RefreshCw size={16} /> Reset
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {summary && (
        <Card data-testid="rx-result-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-lg">Extraction result</CardTitle>
              <div className="flex items-center gap-3">
                <Badge variant="outline">class · {summary.classification?.image_class}</Badge>
                <Badge variant="outline">conf · {(summary.conf || 0).toFixed(2)}</Badge>
                {result.verified && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border"><CheckCircle2 size={12} className="mr-1" /> verified</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <GuidanceBadge guidance={summary.guidance} />
            <ProviderChip runs={summary.runs} primary={summary.provider} fallbackUsed={summary.fallback} />
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-slate-500 text-xs">Doctor</div><div>{result.extraction?.doctor_name || "—"}</div></div>
              <div><div className="text-slate-500 text-xs">Hospital</div><div>{result.extraction?.hospital || "—"}</div></div>
              <div><div className="text-slate-500 text-xs">Date</div><div>{result.extraction?.prescription_date || "—"}</div></div>
              <div><div className="text-slate-500 text-xs">Patient</div><div>{result.extraction?.patient_name || "—"}</div></div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-medium">Medicines ({meds.length})</div>
                <Button size="sm" variant="outline" data-testid="rx-add-med-btn"
                        onClick={() => setMeds([...meds, { name: "", confidence: 0, needs_human_verification: true }])}>
                  Add medicine
                </Button>
              </div>
              <div className="space-y-2">
                {meds.length === 0 && <div className="text-sm text-slate-500 p-4 border rounded-lg">No medicines extracted. Add manually or re-upload a clearer image.</div>}
                {meds.map((m, i) => (
                  <MedicineRow key={m.id || m._key || `med-${i}`} index={i} m={m}
                                onChange={(nm) => setMeds(meds.map((x, k) => k === i ? nm : x))}
                                onRemove={(k) => setMeds(meds.filter((_, j) => j !== k))} />
                ))}
              </div>
            </div>

            <div>
              <Label>Staff remarks</Label>
              <Textarea data-testid="rx-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Notes for this verification (optional)" />
            </div>

            <div className="flex gap-2">
              <Button data-testid="rx-confirm-btn" onClick={onConfirm} disabled={result.verified || meds.length === 0}>
                <CheckCircle2 size={16} /> Confirm &amp; lock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Recent extractions</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="rx-recent-list">
            {recent.length === 0 && <div className="text-sm text-slate-500">Nothing yet.</div>}
            {recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border rounded-lg p-3">
                <div>
                  <div className="font-mono text-xs text-slate-500">{r.id}</div>
                  <div>{r.classification?.image_class || "?"} · conf {(r.confidence_score || 0).toFixed(2)} · {r.extraction?.medicines?.length || 0} meds</div>
                </div>
                <div className="flex items-center gap-2">
                  {r.fallback_used && <Badge variant="outline">fallback</Badge>}
                  {r.verified ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">verified</Badge>
                              : <Badge variant="outline">pending</Badge>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
